import React, { useCallback, useMemo, useState } from 'react';
import { useStore } from '@nanostores/react';
import {
  ChevronRight,
  Folder,
  FolderOpen,
  File,
  FileCode2,
  FilePlus2,
  FolderPlus,
  RefreshCw,
  ChevronsDownUp,
  ChevronsUpDown,
  MoreHorizontal,
} from 'lucide-react';
import type { WebContainer } from '@webcontainer/api';
import { Button, cn, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ideoz/ui';
import { workbenchStore } from '../stores/workbench';
import { activeTemplateStore } from '../templates/headlessTemplate';
import { editorStore, selectFile } from '../stores/editor';
import { getMergedFilePaths, materializeTemplateFileIfNeeded } from '../utils/mergedFiles';
import { persistUserFile } from '../runtime/tool-adapters/writeFile';

export interface FileTreeProps {
  projectName?: string;
  webcontainer: WebContainer | null;
}

interface FileTreeNode {
  name: string;
  path: string;
  children?: FileTreeNode[];
}

function buildTree(paths: string[]): FileTreeNode[] {
  const root: FileTreeNode[] = [];
  const sorted = [...paths].sort((a, b) => a.localeCompare(b));

  for (const path of sorted) {
    const parts = path.split('/');
    let current = root;

    for (let i = 0; i < parts.length; i++) {
      const name = parts[i];
      const fullPath = parts.slice(0, i + 1).join('/');
      const isFile = i === parts.length - 1;

      let existing = current.find((n) => n.name === name);
      if (!existing) {
        existing = { name, path: fullPath, children: isFile ? undefined : [] };
        current.push(existing);
      }
      if (!isFile && existing.children) {
        current = existing.children;
      }
    }
  }

  function sortLevel(nodes: FileTreeNode[]) {
    nodes.sort((a, b) => {
      const ad = !!a.children;
      const bd = !!b.children;
      if (ad !== bd) return ad ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    for (const n of nodes) {
      if (n.children) sortLevel(n.children);
    }
  }
  sortLevel(root);
  return root;
}

function collectDirMeta(
  nodes: FileTreeNode[],
  depth: number,
  out: { path: string; depth: number }[],
): void {
  for (const n of nodes) {
    if (n.children) {
      out.push({ path: n.path, depth });
      collectDirMeta(n.children, depth + 1, out);
    }
  }
}

function getFileIcon(name: string) {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  const colorMap: Record<string, string> = {
    ts: 'text-blue-500',
    tsx: 'text-blue-500',
    js: 'text-yellow-600',
    jsx: 'text-yellow-600',
    css: 'text-purple-500',
    scss: 'text-purple-500',
    html: 'text-orange-500',
    json: 'text-amber-600',
  };
  const color = colorMap[ext];
  if (color === 'text-blue-500') return <FileCode2 size={14} className={cn(color, 'shrink-0')} />;
  return <File size={14} className={cn(color ?? 'text-muted-foreground', 'shrink-0')} />;
}

function TreeNode({
  node,
  depth,
  isExpanded,
  onToggleDir,
}: {
  node: FileTreeNode;
  depth: number;
  isExpanded: (path: string, defaultDepth: number) => boolean;
  onToggleDir: (path: string, defaultDepth: number) => void;
}) {
  const selectedFile = useStore(editorStore.selectedFile);
  const isDir = !!node.children;
  const expanded = isDir ? isExpanded(node.path, depth) : false;
  const isSelected = !isDir && node.path === selectedFile;
  const guideInset = 10 + depth * 14;

  return (
    <div className="relative">
      {depth > 0 ? (
        <span
          className="absolute top-0 bottom-0 w-px bg-border/60"
          style={{ left: `${guideInset - 6}px` }}
          aria-hidden
        />
      ) : null}
      <button
        type="button"
        onClick={() => {
          if (isDir) {
            onToggleDir(node.path, depth);
          } else {
            materializeTemplateFileIfNeeded(node.path);
            selectFile(node.path);
          }
        }}
        className={cn(
          'group w-full text-left py-0.5 pr-2 font-mono text-[12px] leading-snug flex items-center gap-1 border-l-2 border-transparent transition-colors',
          'hover:bg-muted/50',
          isSelected && 'bg-background border-l-primary shadow-sm text-foreground',
          !isSelected && 'text-foreground/90',
        )}
        style={{ paddingLeft: `${guideInset}px` }}
      >
        <span className="flex w-4 shrink-0 justify-center items-center" aria-hidden>
          {isDir ? (
            expanded ? (
              <FolderOpen size={14} className="text-amber-500 shrink-0" />
            ) : (
              <Folder size={14} className="text-amber-500 shrink-0" />
            )
          ) : (
            getFileIcon(node.name)
          )}
        </span>
        {isDir && (
          <span className="flex w-3 shrink-0 justify-center items-center" aria-hidden>
            <ChevronRight
              size={12}
              className={cn('text-muted-foreground transition-transform duration-100', expanded && 'rotate-90')}
            />
          </span>
        )}
        <span className="truncate">{node.name}</span>
      </button>
      {isDir && expanded && node.children?.map((child) => (
        <TreeNode
          key={child.path}
          node={child}
          depth={depth + 1}
          isExpanded={isExpanded}
          onToggleDir={onToggleDir}
        />
      ))}
    </div>
  );
}

export function FileTree({ projectName = 'Project', webcontainer }: FileTreeProps) {
  const files = useStore(workbenchStore.files);
  const activeTemplate = useStore(activeTemplateStore);
  const paths = useMemo(() => getMergedFilePaths(), [files, activeTemplate]);
  const tree = useMemo(() => buildTree(paths), [paths]);

  const dirMeta = useMemo(() => {
    const out: { path: string; depth: number }[] = [];
    collectDirMeta(tree, 0, out);
    return out;
  }, [tree]);

  const [expandedOverrides, setExpandedOverrides] = useState<Record<string, boolean>>({});
  const [, setRefreshTick] = useState(0);

  const isExpanded = useCallback(
    (path: string, defaultDepth: number) => {
      if (Object.prototype.hasOwnProperty.call(expandedOverrides, path)) {
        return expandedOverrides[path]!;
      }
      return defaultDepth < 2;
    },
    [expandedOverrides],
  );

  const onToggleDir = useCallback(
    (path: string, defaultDepth: number) => {
      setExpandedOverrides((prev: Record<string, boolean>) => {
        const cur = Object.prototype.hasOwnProperty.call(prev, path)
          ? prev[path]!
          : defaultDepth < 2;
        return { ...prev, [path]: !cur };
      });
    },
    [],
  );

  const collapseAll = useCallback(() => {
    const next: Record<string, boolean> = {};
    for (const { path } of dirMeta) {
      next[path] = false;
    }
    setExpandedOverrides(next);
  }, [dirMeta]);

  const expandAll = useCallback(() => {
    const next: Record<string, boolean> = {};
    for (const { path } of dirMeta) {
      next[path] = true;
    }
    setExpandedOverrides(next);
  }, [dirMeta]);

  const refreshTree = useCallback(() => {
    setRefreshTick((t: number) => t + 1);
  }, []);

  const addFile = useCallback(async () => {
    const existing = new Set(getMergedFilePaths());
    let name = 'untitled.tsx';
    let i = 1;
    while (existing.has(name)) {
      name = `untitled-${i}.tsx`;
      i++;
    }
    if (!webcontainer) {
      workbenchStore.files.setKey(name, { content: '', modified: true });
    } else {
      await persistUserFile(webcontainer, name, '', false);
    }
    selectFile(name);
  }, [webcontainer]);

  const addFolder = useCallback(async () => {
    const existing = new Set(getMergedFilePaths());
    let folder = 'new-folder';
    let i = 1;
    const placeholderPath = (f: string) => `${f}/.gitkeep`;
    while (existing.has(placeholderPath(folder))) {
      folder = `new-folder-${i}`;
      i++;
    }
    const path = placeholderPath(folder);
    if (!webcontainer) {
      workbenchStore.files.setKey(path, { content: '', modified: true });
    } else {
      await persistUserFile(webcontainer, path, '', false);
    }
    selectFile(path);
  }, [webcontainer]);

  const iconBtnClass =
    'h-7 w-7 p-0 text-muted-foreground hover:text-foreground border-0 bg-transparent shadow-sm';

  if (tree.length === 0) {
    return (
      <TooltipProvider delayDuration={400}>
        <div className="flex flex-col h-full min-h-0 border-b border-border/40">
          <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border/40 shrink-0 glass-subtle">
            <span
              className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground truncate flex-1 min-w-0"
              title={projectName}
            >
              {projectName}
            </span>
          </div>
          <div className="p-3 text-[12px] font-mono text-muted-foreground italic">No files yet</div>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={400}>
      <div className="flex flex-col h-full min-h-0">
        <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border/40 shrink-0 glass-subtle">
          <span
            className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground truncate flex-1 min-w-0 pr-1"
            title={projectName}
          >
            {projectName}
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className={iconBtnClass} aria-label="New file" onClick={() => void addFile()}>
                <FilePlus2 size={15} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">New file</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className={iconBtnClass} aria-label="New folder" onClick={() => void addFolder()}>
                <FolderPlus size={15} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">New folder</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className={iconBtnClass} aria-label="Refresh explorer" onClick={refreshTree}>
                <RefreshCw size={15} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Refresh</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className={iconBtnClass} aria-label="Collapse all folders" onClick={collapseAll}>
                <ChevronsDownUp size={15} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Collapse all</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className={iconBtnClass} aria-label="Expand all folders" onClick={expandAll}>
                <ChevronsUpDown size={15} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Expand all</TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className={iconBtnClass} aria-label="More explorer actions">
                <MoreHorizontal size={15} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={() => void addFile()}>New file…</DropdownMenuItem>
              <DropdownMenuItem onClick={() => void addFolder()}>New folder…</DropdownMenuItem>
              <DropdownMenuItem onClick={expandAll}>Expand all</DropdownMenuItem>
              <DropdownMenuItem onClick={collapseAll}>Collapse all</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="py-1 overflow-y-auto overflow-x-hidden flex-1 min-h-0">
          <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground select-none">Explorer</p>
          {tree.map((node: FileTreeNode) => (
            <TreeNode
              key={node.path}
              node={node}
              depth={0}
              isExpanded={isExpanded}
              onToggleDir={onToggleDir}
            />
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
}
